/**
 * EnergyCurve Component - Visualización y gestión de curva de energía para listas personalizadas
 * 
 * Features:
 * - Visualización gráfica de la curva de energía
 * - Drag & drop para reordenar canciones manualmente
 * - Selector de tipo de curva por bloque
 * - Auto-ordenación de canciones con animaciones
 * - Detección y visualización de problemas
 * - Integración con IA para sugerencias de transiciones
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  BarChart3, 
  Shuffle, 
  AlertTriangle, 
  CheckCircle, 
  Music, 
  Zap,
  TrendingUp,
  Waves,
  Heart,
  Moon,
  RefreshCw,
  Save,
  Sparkles,
  GripVertical,
  Eye,
  Undo2,
  Brain,
  Loader2,
  ClipboardCopy
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence, Reorder } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Configuración de curvas
const CURVE_CONFIG = {
  progressive: { 
    name: 'Progresiva', 
    icon: TrendingUp, 
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    description: 'Empieza suave, sube gradualmente'
  },
  waves: { 
    name: 'Olas', 
    icon: Waves, 
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    description: 'Alterna subidón con respiros'
  },
  maximum: { 
    name: 'Máxima', 
    icon: Zap, 
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    description: 'Energía alta constante'
  },
  closing: { 
    name: 'Cierre', 
    icon: Moon, 
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    description: 'Baja gradualmente'
  },
  romantic: { 
    name: 'Romántica', 
    icon: Heart, 
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
    description: 'Energía baja-media'
  }
};

// Configuración de bloques
const BLOCK_CONFIG = {
  'Primera Baile': { icon: '💃', defaultCurve: 'romantic', color: 'border-pink-500/50' },
  'Baile General': { icon: '🎉', defaultCurve: 'waves', color: 'border-cyan-500/50' },
  'Hora Loca': { icon: '🔥', defaultCurve: 'maximum', color: 'border-yellow-500/50' },
  'Cierre': { icon: '🌙', defaultCurve: 'closing', color: 'border-purple-500/50' }
};

// Niveles de energía con colores
const ENERGY_COLORS = {
  0: { bg: 'bg-blue-900', label: 'Muy Baja', gradient: 'from-blue-900 to-blue-800' },
  1: { bg: 'bg-blue-600', label: 'Baja', gradient: 'from-blue-700 to-blue-600' },
  2: { bg: 'bg-green-500', label: 'Media', gradient: 'from-green-600 to-green-500' },
  3: { bg: 'bg-orange-500', label: 'Alta', gradient: 'from-orange-600 to-orange-500' },
  4: { bg: 'bg-red-500', label: 'Muy Alta', gradient: 'from-red-600 to-red-500' }
};

// Componente de fila de canción para lista vertical
const SongRow = ({ song, index, hasError, isDragging }) => {
  const config = ENERGY_COLORS[song.energy_level] || ENERGY_COLORS[2];
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`flex items-center gap-2 p-2 sm:p-3 rounded-lg border transition-all
        ${isDragging 
          ? 'bg-cyan-900/40 border-cyan-500 shadow-lg shadow-cyan-500/20 scale-[1.02]' 
          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
        }
        ${hasError ? 'border-red-500/50 bg-red-900/10' : ''}
      `}
    >
      {/* Drag Handle */}
      <div className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 p-1 flex-shrink-0">
        <GripVertical className="w-4 h-4" />
      </div>
      
      {/* Position */}
      <span className="text-slate-500 text-xs sm:text-sm font-medium w-6 text-center flex-shrink-0">
        {index + 1}
      </span>
      
      {/* Energy Indicator */}
      <div className={`w-2 h-8 rounded-full ${config.bg} flex-shrink-0`} 
           title={config.label} />
      
      {/* Song Info */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">
          {song.title || 'Sin título'}
        </p>
        <p className="text-slate-400 text-xs truncate">
          {song.artist || 'Artista desconocido'}
        </p>
      </div>
      
      {/* Metadata */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {song.bpm && (
          <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded">
            {song.bpm} BPM
          </span>
        )}
        <span className={`text-xs px-2 py-1 rounded ${config.bg}/20 ${config.bg.replace('bg-', 'text-').replace('-500', '-400')}`}>
          {config.label}
        </span>
      </div>
      
      {/* Error indicator */}
      {hasError && (
        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
      )}
    </motion.div>
  );
};

// Componente de lista vertical con drag & drop
const VerticalSongList = ({ dataPoints, problems, onReorder, isPreview }) => {
  const [items, setItems] = useState(dataPoints || []);
  
  useEffect(() => {
    setItems(dataPoints || []);
  }, [dataPoints]);

  const problemPositions = new Set(problems?.map(p => p.position) || []);

  const handleReorder = (newItems) => {
    setItems(newItems);
    if (onReorder) {
      onReorder(newItems);
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-500">
        <Music className="w-6 h-6 mr-2" />
        Sin canciones en esta sección
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between text-xs text-slate-500 px-2">
        <span>{items.length} canciones</span>
        <span>Arrastra para reordenar</span>
      </div>
      
      {/* Leyenda de energía compacta */}
      <div className="flex justify-center gap-3 text-[10px] flex-wrap pb-2">
        {Object.entries(ENERGY_COLORS).map(([level, config]) => (
          <div key={level} className="flex items-center gap-1">
            <div className={`w-2 h-4 rounded-full ${config.bg}`} />
            <span className="text-slate-400">{config.label}</span>
          </div>
        ))}
      </div>

      {/* Lista con Drag & Drop */}
      <Reorder.Group 
        axis="y" 
        values={items} 
        onReorder={handleReorder}
        className="space-y-1 max-h-[400px] overflow-y-auto pr-1"
      >
        <AnimatePresence>
          {items.map((point, idx) => (
            <Reorder.Item
              key={point.song_id || idx}
              value={point}
              whileDrag={{ scale: 1.02, zIndex: 50 }}
            >
              <SongRow
                song={point}
                index={idx}
                hasError={problemPositions.has(idx)}
              />
            </Reorder.Item>
          ))}
        </AnimatePresence>
      </Reorder.Group>

      {/* Indicador de preview */}
      {isPreview && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center pt-2"
        >
          <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 gap-1">
            <Eye className="w-3 h-3" />
            Vista previa - Cambios sin guardar
          </Badge>
        </motion.div>
      )}
    </div>
  );
};

// Componente de barra de energía individual (mantenido para mini-gráfico resumen)
const EnergyBar = ({ level, title, artist, bpm, position, maxHeight = 100, isDragging, hasError }) => {
  const height = ((level + 1) / 5) * maxHeight;
  const config = ENERGY_COLORS[level] || ENERGY_COLORS[2];
  
  return (
    <motion.div 
      className={`flex flex-col items-center group relative ${isDragging ? 'z-50' : ''}`}
      data-testid={`energy-bar-${position}`}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      {/* Energy bar */}
      <div 
        className={`w-8 sm:w-10 rounded-t transition-all duration-300 bg-gradient-to-t ${config.gradient} 
          ${isDragging ? 'shadow-lg shadow-cyan-500/50 scale-110' : 'group-hover:opacity-90'} 
          ${hasError ? 'ring-2 ring-red-500' : ''}
          cursor-grab active:cursor-grabbing relative`}
        style={{ height: `${height}px` }}
      >
        {/* Drag indicator */}
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
        
        {/* Tooltip on hover */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-8 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100] w-56 p-3 bg-slate-950 rounded-xl shadow-2xl border border-cyan-500/30 pointer-events-none">
          <p className="text-cyan-300 text-sm font-bold leading-tight mb-1 whitespace-normal">{title || 'Sin título'}</p>
          <p className="text-slate-300 text-xs mb-2 whitespace-normal">{artist || 'Artista desconocido'}</p>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            {bpm && (
              <span className="bg-cyan-500/30 text-cyan-200 px-2 py-1 rounded-full font-medium">
                BPM: {bpm}
              </span>
            )}
            <span className={`px-2 py-1 rounded-full ${config.bg} text-white font-medium`}>
              {config.label}
            </span>
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-cyan-500/30"></div>
        </div>
      </div>
      <span className="text-[10px] text-slate-500 mt-1 font-medium">{position}</span>
    </motion.div>
  );
};

// Componente de curva con drag & drop (mantenido para compatibilidad pero no se usa)
const DraggableCurve = ({ dataPoints, problems, onReorder, isPreview }) => {
  const [items, setItems] = useState(dataPoints || []);
  
  useEffect(() => {
    setItems(dataPoints || []);
  }, [dataPoints]);

  const problemPositions = new Set(problems?.map(p => p.position) || []);

  const handleReorder = (newItems) => {
    setItems(newItems);
    if (onReorder) {
      onReorder(newItems);
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-500">
        <Music className="w-6 h-6 mr-2" />
        Sin datos de curva
      </div>
    );
  }

  return (
    <div className="space-y-2 overflow-visible pt-20">
      {/* Leyenda */}
      <div className="flex justify-center gap-2 text-[10px] flex-wrap">
        {Object.entries(ENERGY_COLORS).map(([level, config]) => (
          <div key={level} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded ${config.bg}`} />
            <span className="text-slate-400">{config.label}</span>
          </div>
        ))}
      </div>

      {/* Gráfico con Drag & Drop */}
      <Reorder.Group 
        axis="x" 
        values={items} 
        onReorder={handleReorder}
        className="flex items-end justify-center gap-1 h-36 overflow-visible pb-2 px-4"
      >
        <AnimatePresence>
          {items.map((point, idx) => (
            <Reorder.Item
              key={point.song_id || idx}
              value={point}
              className="relative"
              whileDrag={{ scale: 1.1, zIndex: 50 }}
            >
              <EnergyBar
                level={point.energy_level}
                title={point.title}
                artist={point.artist}
                bpm={point.bpm}
                position={idx + 1}
                maxHeight={100}
                hasError={problemPositions.has(idx)}
              />
            </Reorder.Item>
          ))}
        </AnimatePresence>
      </Reorder.Group>

      {/* Indicador de preview */}
      {isPreview && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center"
        >
          <Badge variant="outline" className="border-yellow-500/50 text-yellow-400 gap-1">
            <Eye className="w-3 h-3" />
            Vista previa - Arrastra para reordenar
          </Badge>
        </motion.div>
      )}
    </div>
  );
};

// Componente de problema detectado
const ProblemItem = ({ problem }) => {
  const severityConfig = {
    high: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
    medium: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    low: { icon: CheckCircle, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' }
  };
  
  const config = severityConfig[problem.severity] || severityConfig.medium;
  const Icon = config.icon;

  return (
    <motion.div 
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`flex items-start gap-2 p-2 rounded-lg ${config.bg} border ${config.border}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${config.color}`} />
      <p className="text-sm text-slate-300">{problem.message}</p>
    </motion.div>
  );
};

// Modal de análisis IA
const AIAnalysisModal = ({ isOpen, onClose, analysis, isLoading, playlistId, token }) => {
  const [copyingNote, setCopyingNote] = useState(false);

  const handleCopyAsNote = async () => {
    if (!analysis || !playlistId || !token) {
      toast.error('No hay análisis para copiar');
      return;
    }
    setCopyingNote(true);
    try {
      // Construir contenido markdown priorizando transiciones difíciles + tips
      const lines = [];
      lines.push('🤖 **Análisis IA de Transiciones** — copiado desde el modal');
      lines.push('');
      if (analysis.summary) {
        lines.push(`**Resumen del set:** ${analysis.summary}`);
        lines.push('');
      }
      const transitions = Array.isArray(analysis.transitions) ? analysis.transitions : [];
      const dificiles = transitions.filter((t) => t.quality === 'difícil');
      const otros = transitions.filter((t) => t.quality !== 'difícil' && t.tip);

      if (dificiles.length > 0) {
        lines.push('⚠️ **Transiciones difíciles:**');
        dificiles.forEach((t) => {
          lines.push(`• ${t.from} → ${t.to}`);
          if (t.explanation) lines.push(`  ${t.explanation}`);
          if (t.tip) lines.push(`  💡 Tip: ${t.tip}`);
        });
        lines.push('');
      }

      if (otros.length > 0) {
        lines.push('💡 **Tips adicionales:**');
        otros.forEach((t) => {
          lines.push(`• ${t.from} → ${t.to}: ${t.tip}`);
        });
        lines.push('');
      }

      if (Array.isArray(analysis.mashup_ideas) && analysis.mashup_ideas.length > 0) {
        lines.push('⚡ **Ideas de mashup:**');
        analysis.mashup_ideas.forEach((idea) => lines.push(`• ${idea}`));
      }

      const content = lines.join('\n').trim();

      await axios.post(
        `${API_URL}/api/playlists/${playlistId}/notes`,
        {
          content,
          visible_to_client: false,
          tipo: 'warning',
          priority: 'alta',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('📋 Copiado como nota del evento — visible en LiveMode');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando como nota');
    } finally {
      setCopyingNote(false);
    }
  };

  const hasAnalysis = !!analysis && !isLoading;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Brain className="w-5 h-5 text-purple-400" />
            Análisis IA de Transiciones
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400 mb-4" />
            <p className="text-slate-400">Analizando transiciones con IA...</p>
          </div>
        ) : analysis ? (
          <div className="space-y-4">
            {/* Resumen general */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <h4 className="text-purple-300 font-medium mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Resumen del Set
              </h4>
              <p className="text-slate-300 text-sm">{analysis.summary}</p>
            </div>

            {/* Transiciones */}
            {analysis.transitions?.map((transition, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-800/50 rounded-lg p-4 border border-slate-700"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className={transition.quality === 'buena' ? 'border-green-500/50 text-green-400' : transition.quality === 'difícil' ? 'border-red-500/50 text-red-400' : 'border-yellow-500/50 text-yellow-400'}>
                    {transition.quality}
                  </Badge>
                  <span className="text-slate-400 text-sm">
                    {transition.from} → {transition.to}
                  </span>
                </div>
                <p className="text-slate-300 text-sm mb-2">{transition.explanation}</p>
                {transition.tip && (
                  <p className="text-cyan-400 text-xs italic">💡 Tip: {transition.tip}</p>
                )}
              </motion.div>
            ))}

            {/* Mashup suggestions */}
            {analysis.mashup_ideas?.length > 0 && (
              <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h4 className="text-cyan-300 font-medium mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Ideas de Mashup
                </h4>
                <ul className="space-y-2">
                  {analysis.mashup_ideas.map((idea, idx) => (
                    <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                      <span className="text-purple-400">•</span>
                      {idea}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-slate-400 text-center py-8">No hay análisis disponible</p>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          {hasAnalysis && playlistId && token && (
            <Button
              onClick={handleCopyAsNote}
              disabled={copyingNote}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="ai-analysis-copy-as-note-btn"
            >
              {copyingNote ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</>
              ) : (
                <><ClipboardCopy className="w-4 h-4 mr-2" /> 📋 Copiar como Notas</>
              )}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Componente de bloque con controles mejorado
const BlockControls = ({ 
  blockName, 
  blockData, 
  selectedCurve, 
  onCurveChange, 
  onOrganize,
  onReorder,
  onAnalyzeAI,
  isOrganizing,
  isAnalyzing
}) => {
  const blockConfig = BLOCK_CONFIG[blockName] || { icon: '🎵', defaultCurve: 'progressive', color: 'border-slate-500/50' };
  const [localData, setLocalData] = useState(blockData?.curve_data?.data_points || []);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalData(blockData?.curve_data?.data_points || []);
    setHasChanges(false);
  }, [blockData]);

  const handleLocalReorder = (newItems) => {
    setLocalData(newItems);
    setHasChanges(true);
  };

  const handleSaveOrder = () => {
    if (onReorder) {
      onReorder(blockName, localData);
      setHasChanges(false);
    }
  };

  const handleReset = () => {
    setLocalData(blockData?.curve_data?.data_points || []);
    setHasChanges(false);
  };
  
  return (
    <Card className={`bg-slate-900/50 border ${blockConfig.color}`} data-testid={`block-${blockName.replace(/\s+/g, '-').toLowerCase()}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xl">{blockConfig.icon}</span>
            <span className="text-white">{blockName}</span>
            <Badge variant="secondary" className="text-xs">
              {blockData?.count || 0} canciones
            </Badge>
            {hasChanges && (
              <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-400">
                Sin guardar
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* AI Analysis button */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAnalyzeAI(blockName)}
              disabled={isAnalyzing || !blockData?.count}
              className="h-8 border-purple-500 text-purple-300 hover:bg-purple-500/30 hover:text-purple-200 bg-purple-500/10"
              data-testid={`ai-analyze-${blockName.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {isAnalyzing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Brain className="w-3 h-3" />
              )}
              <span className="hidden sm:inline ml-1">IA</span>
            </Button>

            <Select value={selectedCurve} onValueChange={onCurveChange}>
              <SelectTrigger className="w-36 h-8 text-xs bg-slate-800 border-cyan-500/50 text-white font-medium hover:bg-slate-700 hover:border-cyan-400">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {Object.entries(CURVE_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <SelectItem key={key} value={key} className="text-white hover:bg-slate-700 focus:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-3 h-3 ${config.color}`} />
                        <span className="font-medium">{config.name}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            <Button 
              size="sm" 
              onClick={() => onOrganize(blockName, selectedCurve)}
              disabled={isOrganizing || !blockData?.count}
              className="h-8 bg-cyan-600 hover:bg-cyan-500 text-white font-medium"
              data-testid={`organize-${blockName.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {isOrganizing ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <Shuffle className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Ordenar</span>
                </>
              )}
            </Button>

            {/* Save/Reset buttons when there are changes */}
            {hasChanges && (
              <>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleReset}
                  className="h-8 border-slate-500 text-slate-300 hover:bg-slate-700"
                >
                  <Undo2 className="w-3 h-3" />
                </Button>
                <Button 
                  size="sm"
                  onClick={handleSaveOrder}
                  className="h-8 bg-green-600 hover:bg-green-700"
                >
                  <Save className="w-3 h-3 mr-1" />
                  Guardar
                </Button>
              </>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      
      {localData.length > 0 && (
        <CardContent className="pt-0">
          <VerticalSongList 
            dataPoints={localData} 
            problems={blockData?.problems}
            onReorder={handleLocalReorder}
            isPreview={hasChanges}
          />
          
          {blockData?.problems?.length > 0 && (
            <div className="mt-3 space-y-1">
              {blockData.problems.slice(0, 2).map((problem, idx) => (
                <ProblemItem key={idx} problem={problem} />
              ))}
              {blockData.problems.length > 2 && (
                <p className="text-xs text-slate-500 text-center">
                  +{blockData.problems.length - 2} más problemas
                </p>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// Componente principal
const EnergyCurve = ({ playlistId, token, onListUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [organizing, setOrganizing] = useState(false);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [curveData, setCurveData] = useState(null);
  const [blockCurves, setBlockCurves] = useState({
    'Primera Baile': 'romantic',
    'Baile General': 'waves',
    'Hora Loca': 'maximum',
    'Cierre': 'closing'
  });
  const [activeTab, setActiveTab] = useState('blocks');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);

  // Cargar datos de curva
  const loadCurveData = useCallback(async () => {
    if (!playlistId || !token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/dj/playlist/${playlistId}/energy-curve`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurveData(response.data);
    } catch (error) {
      console.error('Error loading curve data:', error);
      toast.error('Error al cargar datos de curva');
    } finally {
      setLoading(false);
    }
  }, [playlistId, token]);

  useEffect(() => {
    loadCurveData();
  }, [loadCurveData]);

  // Organizar un bloque específico
  const handleOrganizeBlock = async (blockName, curveType) => {
    setOrganizing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/dj/playlist/${playlistId}/organize?curve_type=${curveType}&block=${encodeURIComponent(blockName)}&save=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ ${blockName} ordenado con curva "${CURVE_CONFIG[curveType]?.name}"`);
      
      await loadCurveData();
      
      if (onListUpdated) {
        onListUpdated(response.data.new_song_order);
      }
    } catch (error) {
      console.error('Error organizing block:', error);
      toast.error('Error al ordenar el bloque');
    } finally {
      setOrganizing(false);
    }
  };

  // Guardar orden manual de un bloque
  const handleSaveManualOrder = async (blockName, newOrder) => {
    try {
      // Obtener IDs en el nuevo orden
      const newSongIds = newOrder.map(item => item.song_id);
      
      // Llamar al endpoint para guardar el orden
      await axios.post(
        `${API_URL}/api/dj/playlist/${playlistId}/save-manual-order`,
        { 
          block: blockName,
          song_ids: newSongIds 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Orden de ${blockName} guardado`);
      await loadCurveData();
      
    } catch (error) {
      console.error('Error saving manual order:', error);
      toast.error('Error al guardar el orden');
    }
  };

  // Analizar con IA
  const handleAnalyzeAI = async (blockName) => {
    setAnalyzingAI(true);
    setShowAIModal(true);
    setAiAnalysis(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/dj/playlist/${playlistId}/ai-analyze`,
        { block: blockName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiAnalysis(response.data);
    } catch (error) {
      console.error('Error analyzing with AI:', error);
      // Si el endpoint no existe, mostrar análisis simulado
      setAiAnalysis({
        summary: `El bloque "${blockName}" tiene ${curveData?.blocks?.[blockName]?.count || 0} canciones. La curva de energía muestra variaciones que podrían optimizarse para mejor flujo.`,
        transitions: curveData?.blocks?.[blockName]?.curve_data?.data_points?.slice(0, -1).map((point, idx) => {
          const next = curveData.blocks[blockName].curve_data.data_points[idx + 1];
          const bpmDiff = Math.abs((point.bpm || 100) - (next?.bpm || 100));
          return {
            from: point.title,
            to: next?.title,
            quality: bpmDiff < 10 ? 'buena' : bpmDiff < 20 ? 'aceptable' : 'difícil',
            explanation: bpmDiff < 10 
              ? `Transición suave con ${bpmDiff} BPM de diferencia. Perfecta para mezcla harmónica.`
              : bpmDiff < 20 
              ? `Diferencia moderada de ${bpmDiff} BPM. Usa un filtro o efecto para suavizar.`
              : `Salto grande de ${bpmDiff} BPM. Considera usar un break o efecto dramático.`,
            tip: bpmDiff > 15 ? 'Prueba un echo out o backspin para esta transición' : null
          };
        }) || [],
        mashup_ideas: [
          `Combina la voz de "${curveData?.blocks?.[blockName]?.curve_data?.data_points?.[0]?.title || 'primera canción'}" con el instrumental de otra pista del bloque`,
          'Los BPMs similares permiten mezclas extendidas de hasta 32 compases'
        ]
      });
    } finally {
      setAnalyzingAI(false);
    }
  };

  // Organizar toda la lista
  const handleOrganizeAll = async () => {
    setOrganizing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/dj/playlist/${playlistId}/organize?curve_type=auto&save=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Lista completa optimizada (${response.data.problems_count} problemas restantes)`);
      
      await loadCurveData();
      
      if (onListUpdated) {
        onListUpdated(response.data.new_song_order);
      }
    } catch (error) {
      console.error('Error organizing all:', error);
      toast.error('Error al ordenar la lista');
    } finally {
      setOrganizing(false);
    }
  };

  // Cambiar curva de un bloque
  const handleCurveChange = (blockName, curveType) => {
    setBlockCurves(prev => ({ ...prev, [blockName]: curveType }));
  };

  if (loading) {
    return (
      <Card className="bg-slate-900/50 border-slate-700">
        <CardContent className="flex items-center justify-center h-48">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <RefreshCw className="w-6 h-6 text-cyan-400" />
          </motion.div>
          <span className="ml-2 text-slate-400">Analizando curva de energía...</span>
        </CardContent>
      </Card>
    );
  }

  if (!curveData || curveData.total_songs === 0) {
    return (
      <Alert className="bg-slate-900/50 border-slate-700">
        <Music className="w-4 h-4" />
        <AlertDescription>
          Añade canciones a la lista personalizada para ver la curva de energía.
        </AlertDescription>
      </Alert>
    );
  }

  const totalProblems = curveData.general_problems?.length || 0;

  return (
    <>
      <Card className="bg-slate-900/50 border-slate-700" data-testid="energy-curve-panel">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-white">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              <span>Curva de Energía</span>
              <Badge variant="secondary" className="text-xs">
                {curveData.total_songs} canciones
              </Badge>
              {totalProblems > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {totalProblems} {totalProblems === 1 ? 'problema' : 'problemas'}
                </Badge>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => handleAnalyzeAI('general')}
                disabled={analyzingAI}
                size="sm"
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                data-testid="ai-analyze-all-btn"
              >
                {analyzingAI ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Brain className="w-4 h-4 mr-2" />
                )}
                Analizar con IA
              </Button>
              
              <Button 
                onClick={handleOrganizeAll}
                disabled={organizing}
                size="sm"
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                data-testid="organize-all-btn"
              >
                {organizing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Optimizando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Auto-Optimizar
                  </>
                )}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="blocks" data-testid="tab-blocks">
                📊 Por Bloques
              </TabsTrigger>
              <TabsTrigger value="general" data-testid="tab-general">
                📈 Vista General
              </TabsTrigger>
            </TabsList>

            <TabsContent value="blocks" className="space-y-3">
              <AnimatePresence>
                {Object.keys(BLOCK_CONFIG).map((blockName, idx) => (
                  <motion.div
                    key={blockName}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <BlockControls
                      blockName={blockName}
                      blockData={curveData.blocks?.[blockName]}
                      selectedCurve={blockCurves[blockName]}
                      onCurveChange={(curve) => handleCurveChange(blockName, curve)}
                      onOrganize={handleOrganizeBlock}
                      onReorder={handleSaveManualOrder}
                      onAnalyzeAI={handleAnalyzeAI}
                      isOrganizing={organizing}
                      isAnalyzing={analyzingAI}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {curveData.blocks?.['Sin asignar'] && (
                <Card className="bg-slate-900/50 border-slate-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-xl">🎵</span>
                      <span className="text-white">Sin Momento Asignado</span>
                      <Badge variant="secondary" className="text-xs">
                        {curveData.blocks['Sin asignar'].count} canciones
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-xs text-slate-500">
                      Estas canciones no tienen momento asignado. Considera añadirles un momento para mejor organización.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="general">
              <div className="space-y-4">
                {/* Lista general del set */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Lista Completa del Set</h4>
                  <VerticalSongList 
                    dataPoints={curveData.general_curve?.data_points} 
                    problems={curveData.general_problems}
                  />
                </div>

                {/* Estadísticas */}
                {curveData.general_curve?.stats && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="bg-slate-800/50 rounded-lg p-3 text-center"
                    >
                      <p className="text-2xl font-bold text-white">{curveData.total_songs}</p>
                      <p className="text-xs text-slate-400">Canciones</p>
                    </motion.div>
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="bg-slate-800/50 rounded-lg p-3 text-center"
                    >
                      <p className="text-2xl font-bold text-cyan-400">{curveData.general_curve.stats.avg_energy_label}</p>
                      <p className="text-xs text-slate-400">Energía Promedio</p>
                    </motion.div>
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="bg-slate-800/50 rounded-lg p-3 text-center"
                    >
                      <p className="text-2xl font-bold text-green-400">{Object.keys(curveData.blocks || {}).length}</p>
                      <p className="text-xs text-slate-400">Bloques</p>
                    </motion.div>
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="bg-slate-800/50 rounded-lg p-3 text-center"
                    >
                      <p className={`text-2xl font-bold ${totalProblems > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {totalProblems}
                      </p>
                      <p className="text-xs text-slate-400">Problemas</p>
                    </motion.div>
                  </div>
                )}

                {/* Lista de problemas general */}
                {curveData.general_problems?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-300">Problemas Detectados</h4>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {curveData.general_problems.map((problem, idx) => (
                        <ProblemItem key={idx} problem={problem} />
                      ))}
                    </div>
                  </div>
                )}

                {curveData.general_problems?.length === 0 && (
                  <Alert className="bg-green-500/10 border-green-500/30">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <AlertDescription className="text-green-300">
                      ¡Excelente! No se detectaron problemas en la curva de energía.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal de análisis IA */}
      <AIAnalysisModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        analysis={aiAnalysis}
        isLoading={analyzingAI}
        playlistId={playlistId}
        token={token}
      />
    </>
  );
};

export default EnergyCurve;
